import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

import { getConsumer } from "@shared/lib/action-cable/consumer"
import {
  highlightChanges,
  snapshotTexts,
} from "@shared/lib/companion-change-highlight"
import { isEditingPage } from "@shared/lib/live-refresh"

// CONEK-9275 — the mobile twin of useQuoteStatusChannel: someone else changed
// this quote's status (the PM released or sent it back, Sales handed it over),
// so the page morph-refreshes in place, flashes what changed and says why.
// Never under a field the user is typing in or an open drawer (the morph
// would close it): they get the "changed, reload when done" toast instead.
// The user's own changes already re-render their page, so they are ignored.
export default class extends Controller {
  static targets = ["updatedToast", "staleToast"]
  static values = { quoteId: String, userId: String, spaceGroupId: String }

  connect() {
    this.subscription = getConsumer(
      this.spaceGroupIdValue || "default",
    ).subscriptions.create(
      { channel: "QuoteStatusChannel", order_id: this.quoteIdValue },
      { received: (data) => this.received(data) },
    )
  }

  disconnect() {
    this.subscription?.unsubscribe()
    this.#forgetPendingMorph()
  }

  received(data) {
    if (data?.type !== "status_changed" || data.order_id !== this.quoteIdValue)
      return
    if (data.actor_id && data.actor_id === this.userIdValue) return

    if (this.#userIsBusy()) {
      this.#showToast(this.hasStaleToastTarget ? this.staleToastTarget : null)
      return
    }

    // A refresh is already on its way: it will carry this change too, so one
    // toast and one flash cover both.
    if (this.pendingMorph) return

    // After the morph, not before: it resets #mobile-toast to the server render.
    const template = this.hasUpdatedToastTarget ? this.updatedToastTarget : null
    const before = snapshotTexts(document.body)
    this.pendingMorph = () => {
      this.#forgetPendingMorph()
      this.#showToast(template)
      highlightChanges(document.body, before)
    }
    document.addEventListener("turbo:morph", this.pendingMorph, { once: true })
    // A refresh that fails, or that Turbo skips because a visit is running,
    // never morphs: release the gate when that visit ends either way, or the
    // page would stop refreshing for good.
    this.releasePendingMorph = () => this.#forgetPendingMorph()
    for (const event of ["turbo:load", "turbo:fetch-request-error"])
      document.addEventListener(event, this.releasePendingMorph, { once: true })
    Turbo.session.refresh(document.baseURI, {
      method: "morph",
      scroll: "preserve",
    })
  }

  // Leaving the page before the morph lands must not toast on the next one.
  #forgetPendingMorph() {
    if (this.pendingMorph)
      document.removeEventListener("turbo:morph", this.pendingMorph)
    for (const event of ["turbo:load", "turbo:fetch-request-error"])
      document.removeEventListener(event, this.releasePendingMorph)
    this.pendingMorph = null
  }

  #userIsBusy() {
    return (
      isEditingPage() ||
      document.querySelector('[data-controller~="ui--drawer"]:not(.hidden)') !==
        null
    )
  }

  #showToast(template) {
    const container = document.getElementById("mobile-toast")
    const toast = template?.content?.firstElementChild?.cloneNode(true)
    if (!container || !toast) return
    toast.id = `toast-quote-status-${Math.random().toString(36).slice(2, 10)}`
    container.append(toast)
  }
}
